import { API_BASE_URL } from 'lib/config/apiEndpoints';
import { SDD_NDD_BADGE_ENABLED } from 'utils/featureFlags';
import { CampaignsResponse, SddDeliveryBanner } from './types';

export { getActiveCampaigns } from './lookup';

export const CAMPAIGNS_CACHE_TAG = 'campaigns';

// product_campaigns accepts ISO-2 only. Region can be GLOBAL (and used to be
// the literal "row"); those must not hit the upstream.
const VALID_COUNTRY_RE = /^[A-Za-z]{2}$/;
const REJECTED_PLACEHOLDERS = new Set(['xx', 'xy', 'zz', 'aa']);

export function isCampaignsCountryCode(country: unknown): country is string {
  if (typeof country !== 'string') return false;
  const trimmed = country.trim();
  if (!trimmed) return false;
  const lower = trimmed.toLowerCase();
  if (lower === 'undefined' || lower === 'null') return false;
  if (REJECTED_PLACEHOLDERS.has(lower)) return false;
  return VALID_COUNTRY_RE.test(trimmed);
}

const SSR_CACHE_TTL_MS = 60_000;
const ssrCampaignsCache = new Map<
  string,
  { data: CampaignsResponse; expiresAt: number }
>();

// Concurrent SSR misses for one country share one upstream call — 8 parallel
// /jp/ requests opened 8 round trips. Server-only: uniform 1000ms budget.
const ssrInflight = new Map<string, Promise<CampaignsResponse>>();

export async function fetchCampaigns(
  country: string,
  ssr = false,
): Promise<CampaignsResponse> {
  if (!isCampaignsCountryCode(country)) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        `[campaigns] skipping fetch — invalid country="${String(country)}"`,
      );
    }
    return { campaigns: [] };
  }

  const cacheKey = country.trim().toUpperCase();
  const isServer = typeof window === 'undefined';

  if (!isServer) return loadCampaigns(cacheKey, ssr, isServer);

  const cached = ssrCampaignsCache.get(cacheKey);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.data;
  }

  const inflight = ssrInflight.get(cacheKey);
  if (inflight) return inflight;

  const pending = loadCampaigns(cacheKey, ssr, isServer).finally(() => {
    ssrInflight.delete(cacheKey);
  });
  ssrInflight.set(cacheKey, pending);
  return pending;
}

/** Drop the process-local 60s map. Called from POST /api/revalidate when Rails sends kind: "campaigns". */
export function clearCampaignsSsrCache(): void {
  ssrCampaignsCache.clear();
  ssrInflight.clear();
}

async function loadCampaigns(
  cacheKey: string,
  ssr: boolean,
  isServer: boolean,
): Promise<CampaignsResponse> {
  const timeout = ssr ? 1000 : 5000;
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => controller.abort('Request timeout'),
      timeout,
    );

    const url = `${API_BASE_URL}/api/web_v1/product_campaigns?country=${encodeURIComponent(
      cacheKey,
    )}`;
    const response = await fetch(url, {
      signal: controller.signal,
      next: { revalidate: 60, tags: [CAMPAIGNS_CACHE_TAG] },
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      console.log(
        `Campaign fetch failed for country="${cacheKey}" (${response.status}): ${response.statusText}`,
      );
      const failed: CampaignsResponse = { campaigns: [], fetchFailed: true };
      // Cache failures too, so an upstream outage costs one round trip per
      // country per minute instead of one per page view.
      if (isServer) {
        ssrCampaignsCache.set(cacheKey, {
          data: failed,
          expiresAt: Date.now() + SSR_CACHE_TTL_MS,
        });
      }
      return failed;
    }

    const data: CampaignsResponse = await response.json();
    if (isServer) {
      ssrCampaignsCache.set(cacheKey, {
        data,
        expiresAt: Date.now() + SSR_CACHE_TTL_MS,
      });
    }
    return data;
  } catch (error) {
    console.error('Error fetching campaigns:', error);
    const failed: CampaignsResponse = { campaigns: [], fetchFailed: true };
    if (isServer) {
      ssrCampaignsCache.set(cacheKey, {
        data: failed,
        expiresAt: Date.now() + SSR_CACHE_TTL_MS,
      });
    }
    return failed;
  }
}

/** Website kill switch, missing key, false flag, or empty copy → banner off. Campaign filtering must not touch this. */
export function normalizeSddDeliveryBanner(
  delivery?: SddDeliveryBanner | null,
): SddDeliveryBanner | null {
  if (!SDD_NDD_BADGE_ENABLED) return null;
  if (!delivery || delivery.sdd_delivery_available !== true) return null;
  const title = typeof delivery.title === 'string' ? delivery.title.trim() : '';
  const cta = typeof delivery.cta === 'string' ? delivery.cta.trim() : '';
  if (!title || !cta) return null;
  const advisory =
    typeof delivery.advisory === 'string' ? delivery.advisory.trim() : '';
  const placeholder =
    typeof delivery.placeholder === 'string' ? delivery.placeholder.trim() : '';
  const mode =
    delivery.mode === 'same_day' ||
    delivery.mode === 'two_hour' ||
    delivery.mode === 'next_day'
      ? delivery.mode
      : undefined;
  const order_by =
    typeof delivery.order_by === 'string' ? delivery.order_by.trim() : '';
  return {
    sdd_delivery_available: true,
    title,
    cta,
    ...(advisory ? { advisory } : {}),
    ...(placeholder ? { placeholder } : {}),
    ...(mode ? { mode } : {}),
    ...(order_by ? { order_by } : {}),
  };
}

